import { useCallback, useEffect, useMemo, useRef } from 'react';
import { getHomeworkById, updateHomework } from '../utils/homeworkService';

export function useContentSaveHandlers({
  setEditorContents, setWhiteboardContents, setWhiteboardCursorPositions,
  whiteboards, isStudent, publishWhiteboardPatch, publishStudentWhiteboardPatch,
  homeworkDataCache, setHomeworkDataCache, homeworkContainers, editorContents,
  allEditorsRef, studentId, lessonId,
}) {
  // Функция для сохранения содержимого редактора (только в локальное состояние, не в файл)
  const saveEditorContent = useCallback((editorId, content) => {
    setEditorContents(prev => ({
      ...prev,
      [editorId]: content
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Функция для сохранения содержимого доски (только в локальное состояние, не в файл)
  const saveWhiteboardContent = useCallback((whiteboardId, content) => {
    setWhiteboardContents(prev => ({
      ...prev,
      [whiteboardId]: content
    }));

    // Сохраняем cursorPosition если он есть в content
    if (content && content.cursorPosition) {
      const whiteboardIdWithoutPrefix = whiteboardId.replace('whiteboard-', '');
      setWhiteboardCursorPositions(prev => ({
        ...prev,
        [whiteboardIdWithoutPrefix]: content.cursorPosition
      }));
    }
    // Publish realtime patch so the other side mirrors changes
    try {
      const targetId = whiteboardId.replace('whiteboard-', '');
      const patch = { updatedAt: Date.now() };
      if (content && content.cursorPosition) {
        patch.cursorPosition = content.cursorPosition;
      }
      if (content && Array.isArray(content.shapes)) {
        // Strip local-only URLs (blob:/data:) — they can't be loaded cross-origin.
        // Images uploaded to Storage will have https:// URLs and pass through fine.
        const lightShapes = content.shapes.map(s => {
          if (!s || typeof s !== 'object') return s;
          if (s.type === 'image' && typeof s.src === 'string' &&
              (s.src.startsWith('data:') || s.src.startsWith('blob:'))) {
            return { ...s, src: '[UPLOADING]' };
          }
          return s;
        });
        patch.type = 'shapes';
        patch.shapes = lightShapes;
        // Include whiteboard type so student can auto-register dynamically-created boards
        const wbMeta = whiteboards.find(wb => String(wb.id) === String(targetId));
        if (wbMeta) patch.wbType = wbMeta.type;
      }
      if (!isStudent && publishWhiteboardPatch) publishWhiteboardPatch(targetId, patch);
      if (isStudent && publishStudentWhiteboardPatch) publishStudentWhiteboardPatch(targetId, patch);
    } catch (e) {
      // ignore
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStudent, whiteboards, publishWhiteboardPatch, publishStudentWhiteboardPatch]);

  // Add these at the top level of your component, after saveEditorContent and saveWhiteboardContent:
  const getContainerContentChangeHandler = useCallback(
    (containerId) => (content) => saveEditorContent(`container-${containerId}`, content),
    [saveEditorContent]
  );

  // Debounced autosave for teacher edits on homework containers AND
  // metadata (title / description / dueDate). Without this teacher edits
  // only live in editorContents + homeworkDataCache + a realtime
  // broadcast — nothing reaches the homework row in Supabase until they
  // explicitly click "Send Homework". On a submitted-then-reviewed
  // homework that button isn't the natural one to click; on a brand-new
  // homework the title/desc/due metadata is also at risk of being lost.
  // 1.5s debounce per homeworkId so we don't slam the DB on every keystroke.
  const homeworkAutosaveTimersRef = useRef({});

  // Single autosave scheduler — used by container content changes AND by
  // the metadata-watch effect below. Reads the latest state at fire time
  // so a late-arriving edit doesn't get clobbered by a stale closure.
  const scheduleHomeworkAutosave = useCallback((hwIdRaw) => {
    const hwId = String(hwIdRaw);
    const timers = homeworkAutosaveTimersRef.current;
    if (timers[hwId]) clearTimeout(timers[hwId]);
    timers[hwId] = setTimeout(async () => {
      try {
        const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(hwId);
        let supabaseId = hwId;
        if (!isUUID) {
          try {
            const existing = await getHomeworkById(hwId);
            if (existing?.id) supabaseId = existing.id;
            else return; // no Supabase row yet — Send will create it
          } catch (_) { return; }
        }

        const cached = homeworkDataCache[hwId] || homeworkDataCache[supabaseId] || {};

        // Latest containers (prefer live editor HTML for the same reason
        // the submit handler does). homeworkContainers can still be empty
        // here even though the container is actively being typed into —
        // it's populated asynchronously, and this scheduler's closure can
        // fire before that state has caught up. The render path for this
        // same container already has a fallback for exactly this gap
        // (falling back to homeworkDataCache's containerContents/containers
        // when homeworkContainers[homeworkId] is still empty — see the
        // currentHomeworkContainers computation above); mirror it here.
        // Without this, an empty containers[] payload was being written to
        // Supabase on every keystroke, silently replacing real content with
        // nothing the moment this race hit.
        let containers = homeworkContainers[hwId]
          || homeworkContainers[supabaseId]
          || [];
        if (containers.length === 0) {
          const cachedContainers = cached.containerContents || cached.containers || [];
          if (Array.isArray(cachedContainers) && cachedContainers.length > 0) {
            containers = cachedContainers.map(cc => ({ id: cc.id, type: 'editor' }));
          }
        }
        const liveHtml = (cid) => {
          try {
            for (const editorId of [`homework-${hwId}-${cid}`, `homework-${supabaseId}-${cid}`]) {
              const editor = allEditorsRef.current?.get?.(editorId);
              if (editor && typeof editor.getHTML === 'function') return editor.getHTML();
            }
          } catch (_) {}
          return null;
        };
        // Hard safety net: if we still couldn't resolve any containers (both
        // homeworkContainers and the homeworkDataCache fallback above came
        // up empty), bail out instead of writing containers: [] — that
        // would silently replace whatever's actually saved with nothing.
        if (containers.length === 0) return;

        const containerContents = containers.map(c => {
          const live = liveHtml(c.id);
          const stale = editorContents[`homework-${hwId}-container-${c.id}`]
            || editorContents[`homework-${supabaseId}-container-${c.id}`];
          return { id: c.id, content: live ?? stale ?? '' };
        });

        const payload = {
          containers: containerContents,
        };
        // Title/description/dueDate are teacher-only fields (students see
        // them read-only in the UI) — never let a student's container
        // autosave touch them.
        if (!isStudent) {
          if (typeof cached.title === 'string') payload.title = cached.title;
          if (typeof cached.description === 'string') payload.description = cached.description;
          if (cached.dueDate !== undefined) payload.dueDate = cached.dueDate;
        }

        console.log('[homework-autosave] saving', { hwId, supabaseId, isStudent, containerCount: containerContents.length, firstContentLen: containerContents[0]?.content?.length });
        await updateHomework(supabaseId, payload);
        console.log('[homework-autosave] saved', { hwId, supabaseId });
      } catch (e) {
        console.warn('[homework-autosave] failed:', e?.message, e);
      }
    }, 1500);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStudent, homeworkDataCache, homeworkContainers, editorContents]);

  // Watch homeworkDataCache and schedule an autosave whenever a homework's
  // metadata actually changes from a teacher edit. This catches title /
  // description / dueDate edits which previously only updated local state.
  //
  // homeworkDataCache also gets populated by loadHomeworkData's initial
  // Supabase fetch when a teacher merely OPENS a homework to review it —
  // that populate alone used to satisfy the "has title/description/dueDate"
  // heuristic below and schedule an autosave 1.5s after simply opening the
  // tab. That autosave reads containers from the teacher's own (freshly
  // mounted, possibly not-yet-hydrated) editorContents/homeworkContainers
  // state and writes it back to Supabase — silently overwriting the
  // student's just-submitted content with a stale/empty snapshot. Track the
  // last-seen metadata per homeworkId and only schedule when it actually
  // differs from that snapshot, so the first observation (the load) just
  // records a baseline instead of firing a save.
  const homeworkMetaSnapshotRef = useRef({});
  useEffect(() => {
    if (isStudent) return;
    Object.entries(homeworkDataCache).forEach(([hwId, data]) => {
      // Heuristic: only track if the cache entry has been populated
      // with at least one meaningful field, to avoid firing the moment
      // a homework tab is added with an empty {} entry.
      if (!data || (!data.title && !data.description && !data.dueDate)) return;
      const prevMeta = homeworkMetaSnapshotRef.current[hwId];
      const currentMeta = { title: data.title, description: data.description, dueDate: data.dueDate };
      homeworkMetaSnapshotRef.current[hwId] = currentMeta;
      if (!prevMeta) return; // first observation — this is the load, not an edit
      if (prevMeta.title === currentMeta.title &&
          prevMeta.description === currentMeta.description &&
          prevMeta.dueDate === currentMeta.dueDate) return;
      scheduleHomeworkAutosave(hwId);
    });
  }, [homeworkDataCache, isStudent, scheduleHomeworkAutosave]);

  const getHomeworkContainerContentChangeHandler = useCallback(
    (homeworkId, containerId) => (content) => {
      // Нормализуем homeworkId (может быть строкой или числом)
      const hwId = String(homeworkId);
      const containerKey = String(containerId);

      // Сохраняем в editorContents
      const key = `homework-${hwId}-container-${containerKey}`;
      setEditorContents(prev => ({
        ...prev,
        [key]: content
      }));

      // Debounced autosave straight to Supabase on every keystroke — for
      // students too, not just teachers. Previously students only ever
      // wrote to Supabase at the moment they clicked "Submit", which meant
      // their container text had to be captured correctly from a live
      // editor instance in that single instant; any race in that capture
      // (or in Follow Mode's editorContents sync, or an expired auth
      // session by submit time) lost the whole homework. Saving
      // continuously as they type means Submit only has to flip `status`
      // — the content is already safely in Supabase by then. Same
      // scheduler used by the metadata-watch effect (teacher-only there)
      // so both share the same 1.5s debounce window per homework.
      scheduleHomeworkAutosave(hwId);

      // Обновляем кеш homework данных
      setHomeworkDataCache(prev => {
        const homeworkData = prev[hwId] || {};
        const containerContents = homeworkData.containerContents || [];
        const existingIndex = containerContents.findIndex(cc => String(cc.id) === containerKey);

        if (existingIndex !== -1) {
          containerContents[existingIndex].content = content;
        } else {
          containerContents.push({ id: containerKey, content });
        }

        const updatedHomeworkData = {
          ...homeworkData,
          containerContents,
          // Сохраняем важные поля, если их нет
          studentId: homeworkData.studentId || studentId || 'demo-student-1',
          lessonId: homeworkData.lessonId || lessonId || 'new',
          status: homeworkData.status || 'assigned',
          title: homeworkData.title || 'Homework',
          dueDate: homeworkData.dueDate || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          assignedAt: homeworkData.assignedAt || new Date().toISOString()
        };

        // Отправляем событие об обновлении homework для студентов
        // (данные сохраняются в Supabase через updateHomework)
        if (isStudent) {
          window.dispatchEvent(new CustomEvent('homeworkUpdated', {
            detail: { homeworkId: hwId, studentId: updatedHomeworkData.studentId }
          }));
        }

        console.log('Homework content updated:', hwId, containerKey);

        return {
          ...prev,
          [hwId]: updatedHomeworkData
        };
      });
    },
    // scheduleHomeworkAutosave MUST be a dep: it's its own useCallback with
    // deps [isStudent, homeworkDataCache, homeworkContainers, editorContents]
    // (a fresh function every keystroke), but this handler's deps here never
    // included it — so this handler kept calling the very first, mount-time
    // closure of scheduleHomeworkAutosave forever. That stale closure reads
    // homeworkDataCache/homeworkContainers as they were at mount (typically
    // still empty), so its own "containers.length === 0 → bail out" safety
    // net always fired — silently, before its first console.log — and the
    // debounced Supabase write never actually ran. This was the root cause
    // of teacher/student homework text edits appearing to never save.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isStudent, studentId, lessonId, scheduleHomeworkAutosave]
  );

  const getWhiteboardContentChangeHandler = useCallback(
    (whiteboardId) => (content) => saveWhiteboardContent(`whiteboard-${whiteboardId}`, content),
    [saveWhiteboardContent]
  );

  // Memoized handlers for each whiteboard to prevent infinite re-renders
  const whiteboardHandlers = useMemo(() => {
    const handlers = {};
    whiteboards.forEach(whiteboard => {
      handlers[whiteboard.id] = (content) => saveWhiteboardContent(`whiteboard-${whiteboard.id}`, content);
    });
    return handlers;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whiteboards, saveWhiteboardContent]);

  return {
    getContainerContentChangeHandler,
    getHomeworkContainerContentChangeHandler,
    getWhiteboardContentChangeHandler,
    whiteboardHandlers,
  };
}
